/**
 * Lazy Route Components
 * 
 * Constitution Section 19.5: React 19 Usage Expectations
 * Route-Level Code Splitting
 * 
 * All route components are lazy-loaded using React.lazy() for code splitting.
 * This reduces initial bundle size and improves load times.
 */

import { lazy, type ComponentType } from 'react';

/**
 * Wrapper around React.lazy that auto-reloads on chunk load failure.
 * After a deploy, old chunk hashes no longer exist on the server.
 * Instead of showing "Unexpected Application Error", reload once.
 */
function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy> {
  return lazy(() =>
    factory().catch((error: Error) => {
      const isChunkError =
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk');

      if (isChunkError) {
        const key = 'chunk_reload_ts';
        const last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last > 30_000) {
          sessionStorage.setItem(key, String(Date.now()));
          window.location.reload();
        }
      }
      throw error;
    }),
  );
}

// ============================================================================
// Public Routes (Lightweight - can be eager loaded if needed)
// ============================================================================
export const LandingPage = lazyWithRetry(() => import('@/pages/LandingPage'));
export const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
export const TerminosPage = lazyWithRetry(() => import('@/pages/TerminosPage'));
export const TratamientoDatosPage = lazyWithRetry(() => import('@/pages/TratamientoDatosPage'));
export const AcercaPage = lazyWithRetry(() => import('@/pages/AcercaPage'));
// Card 7AZtqGE9 — landing dedicada GoFest 26-ago, destino del QR físico.
export const Auth0Callback = lazyWithRetry(() => import('@/components/Auth0Callback'));
export const InvitationAccept = lazyWithRetry(() => import('@/pages/InvitationAccept').then(module => ({ default: module.InvitationAccept })));

// ============================================================================
// Chat Routes (Heavy - includes chat components, React Query, etc.)
// ============================================================================
export const ChatPage = lazyWithRetry(() => import('@/pages/ChatPage'));

// ============================================================================
// Post-wizard financial screens (Card jMJzJXGJ — GoFest modulos 2 + 4)
// ============================================================================
export const CapacidadPage = lazyWithRetry(() => import('@/pages/CapacidadPage'));
export const FinanciacionPage = lazyWithRetry(() => import('@/pages/FinanciacionPage'));

// ============================================================================
// Search Routes (Feature 053: Hybrid UI-Chat Sync)
// ============================================================================
export const HybridSearchPage = lazyWithRetry(() => import('@/pages/HybridSearchPage').then(module => ({ default: module.HybridSearchPage })));

// ============================================================================
// Comparison Routes (Feature 056: Property Comparison Frontend)
// ============================================================================
export const SharedComparisonPage = lazyWithRetry(() => import('@/pages/SharedComparisonPage'));
export const PublicProjectPage = lazyWithRetry(() => import('@/pages/PublicProjectPage'));

// ============================================================================
// Admin Routes (Heavy - includes admin components, analytics, etc.)
// ============================================================================
export const AdminCompaniesPage = lazyWithRetry(() => import('@/pages/admin/AdminCompaniesPage'));
export const AdminCompanyDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminCompanyDetailPage'));
export const AdminCompanyMembersPage = lazyWithRetry(() => import('@/pages/admin/AdminCompanyMembersPage'));
export const AdminPropertiesPage = lazyWithRetry(() => import('@/pages/admin/AdminPropertiesPage'));
export const AdminPropertyDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminPropertyDetailPage'));
export const AdminProjectsPage = lazyWithRetry(() => import('@/pages/admin/AdminProjectsPage'));
export const AdminLeadsPage = lazyWithRetry(() => import('@/pages/admin/AdminLeadsPage'));
export const AdminCajasPage = lazyWithRetry(() => import('@/pages/admin/CajasPage'));
export const AnalyticsPage = lazyWithRetry(() => import('@/pages/admin/AnalyticsPage'));

// ============================================================================
// Main Application Routes (Heavy - includes forms, data tables, etc.)
// ============================================================================

// Dashboard
export const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'));

// Market Insights Dashboard (B2B — card 6a2dc9e6 W6)
export const MarketInsightsPage = lazyWithRetry(() => import('@/pages/dashboard/MarketInsightsPage'));

// Company Analytics Dashboard (B2B — card 76w0BlgU)
export const CompanyAnalyticsPage = lazyWithRetry(() => import('@/pages/dashboard/CompanyAnalyticsPage'));

// Projects
export const ProjectsPage = lazyWithRetry(() => import('@/pages/ProjectsPage'));
export const CreateProjectPage = lazyWithRetry(() => import('@/pages/CreateProjectPage'));
export const ProjectDetailPage = lazyWithRetry(() => import('@/pages/ProjectDetailPage'));
export const EditProjectPage = lazyWithRetry(() => import('@/pages/EditProjectPage'));
export const AddPropertyPage = lazyWithRetry(() => import('@/pages/AddPropertyPage'));
export const ProjectPropertiesPage = lazyWithRetry(() => import('@/pages/ProjectPropertiesPage'));
export const EditProjectPropertyPage = lazyWithRetry(() => import('@/pages/EditProjectPropertyPage'));

// Property Models
export const CreatePropertyModelPage = lazyWithRetry(() => import('@/pages/CreatePropertyModelPage'));
export const EditPropertyModelPage = lazyWithRetry(() => import('@/pages/EditPropertyModelPage'));
export const PropertyModelViewPage = lazyWithRetry(() => import('@/pages/PropertyModelViewPage'));

// Properties
export const PropertiesPage = lazyWithRetry(() => import('@/pages/PropertiesPage'));
export const CreateIndependentPropertyPage = lazyWithRetry(() => import('@/pages/CreateIndependentPropertyPage'));
export const PropertyDetailPage = lazyWithRetry(() => import('@/pages/PropertyDetailPage'));
export const EditPropertyPage = lazyWithRetry(() => import('@/pages/EditPropertyPage'));

// Companies
export const Companies = lazyWithRetry(() => import('@/pages/Companies').then(module => ({ default: module.Companies })));
export const CompanyCreatePage = lazyWithRetry(() => import('@/pages/CompanyCreatePage'));
export const CompanyDetail = lazyWithRetry(() => import('@/pages/CompanyDetail').then(module => ({ default: module.CompanyDetail })));
export const CompanyDetailPage = lazyWithRetry(() => import('@/pages/CompanyDetailPage'));
export const CompanyEditPage = lazyWithRetry(() => import('@/pages/CompanyEditPage'));
export const CompanyMembersPage = lazyWithRetry(() => import('@/pages/CompanyMembersPage').then(module => ({ default: module.CompanyMembersPage })));
export const CompanySettingsPage = lazyWithRetry(() => import('@/pages/CompanySettingsPage'));
export const CompanyCustomFieldsSettingsPage = lazyWithRetry(() => import('@/pages/CompanyCustomFieldsSettingsPage'));

// Invitations
export const MyInvitationsPage = lazyWithRetry(() => import('@/pages/MyInvitationsPage').then(module => ({ default: module.MyInvitationsPage })));

// Leads (card V57jW8x8 — página única role-aware; `MyLeadsPage` se consolidó aquí).
export const LeadsPage = lazyWithRetry(() => import('@/pages/LeadsPage'));

// Favorites (vJgEO72n)
export const FavoritesPage = lazyWithRetry(() => import('@/pages/FavoritesPage'));

// Profile
export const ProfilePage = lazyWithRetry(() => import('@/pages/ProfilePage'));

// AI Assistant Studio
export const AIAssistantStudioPage = lazyWithRetry(() => import('@/pages/ai-studio/AIAssistantStudioPage'));

// Property Wizard (conversational flow — demo/mock)
export const PropertyWizardPage = lazyWithRetry(() => import('@/pages/property-wizard/PropertyWizardPage'));

// Pilots — F1 weekly report wizard (card bfkw44PD — M2.5)
export const WeeklyReportWizardPage = lazyWithRetry(() =>
  import('@/features/pilots/pages/WeeklyReportWizardPage').then((m) => ({
    default: m.WeeklyReportWizardPage,
  })),
);

// Pilots — Dashboard global (card f7661DJi — M3.2)
export const PilotsListPage = lazyWithRetry(() =>
  import('@/features/pilots/pages/PilotsListPage').then((m) => ({
    default: m.PilotsListPage,
  })),
);

// Pilots — Detail page (card jG2r7wcx — M3.3)
export const PilotDetailPage = lazyWithRetry(() =>
  import('@/features/pilots/pages/PilotDetailPage').then((m) => ({
    default: m.PilotDetailPage,
  })),
);

// Pilots — F2 lead evaluation page (card OX7FNB1q — M4.1)
export const LeadEvaluationPage = lazyWithRetry(() =>
  import('@/features/pilots/pages/LeadEvaluationPage').then((m) => ({
    default: m.LeadEvaluationPage,
  })),
);

// Pilots — F2 asesor inbox de evaluaciones pendientes (card OX7FNB1q — M4.1)
export const MyPendingEvaluationsPage = lazyWithRetry(() =>
  import('@/features/pilots/pages/MyPendingEvaluationsPage').then((m) => ({
    default: m.MyPendingEvaluationsPage,
  })),
);

// Pilots — F3 committee note (card Rklj0Tsw — M4.2). Solo admin:pilot.
export const CommitteeNotePage = lazyWithRetry(() =>
  import('@/features/pilots/pages/CommitteeNotePage').then((m) => ({
    default: m.CommitteeNotePage,
  })),
);

// Pilots — M4.3 lead assignment UI (card XeUTytyd). Solo admin:pilot.
export const LeadsAssignmentPage = lazyWithRetry(() =>
  import('@/features/pilots/pages/LeadsAssignmentPage').then((m) => ({
    default: m.LeadsAssignmentPage,
  })),
);

// Pilots — M4.4 new pilot wizard 4 pasos (card mztNXRf9). Solo admin:pilot.
export const NewPilotWizardPage = lazyWithRetry(() =>
  import('@/features/pilots/pages/NewPilotWizardPage').then((m) => ({
    default: m.NewPilotWizardPage,
  })),
);

// ============================================================================
// Admin Components (Heavy)
// ============================================================================
export const SemanticTuningPanel = lazyWithRetry(() => import('@/components/Admin/SemanticTuningPanel'));

// ============================================================================
// Test Routes (Lightweight - can be eager loaded if needed)
// ============================================================================
export const BackendTestPage = lazyWithRetry(() => import('@/pages/BackendTestPage'));
export const TestPage = lazyWithRetry(() => import('@/pages/TestPage'));
export const SimpleTestPage = lazyWithRetry(() => import('@/pages/SimpleTestPage'));
export const TestDirectPage = lazyWithRetry(() => import('@/pages/TestDirectPage'));
export const AuthTestComponent = lazyWithRetry(() => import('@/components/AuthTestComponent'));
